// Cache TTL (Time To Live) in milliseconds
export const CACHE_TTL = {
  PROJECTS: 5 * 60 * 1000, // 5 minutes
  PRODUCTS: 3 * 60 * 1000, // 3 minutes
  DASHBOARD_STATS: 10 * 60 * 1000, // 10 minutes
  CATEGORIES: 30 * 60 * 1000, // 30 minutes (rarely changes)
  USER_PROFILE: 5 * 60 * 1000, // 5 minutes
  SHARED_PROJECTS: 5 * 60 * 1000, // 5 minutes
  FOLLOWS: 5 * 60 * 1000, // 5 minutes
};

// Store cache metadata separately
const CACHE_META_KEY = 'likiee_cache_meta';

/**
 * Check if localStorage is available
 * @returns {boolean} True if localStorage is available
 */
const isLocalStorageAvailable = () => {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

/**
 * Validate cache key
 * @param {string} key - Cache key to validate
 * @throws {Error} If key is invalid
 */
const validateCacheKey = key => {
  if (!key || typeof key !== 'string') {
    throw new Error('Cache key must be a non-empty string');
  }
  if (key.length > 100) {
    throw new Error('Cache key must be less than 100 characters');
  }
};

/**
 * Get cache metadata from localStorage
 * @returns {Object} Cache metadata object with timestamps
 */
export const getCacheMeta = () => {
  if (!isLocalStorageAvailable()) {
    console.warn('localStorage is not available, cache disabled');
    return {};
  }

  try {
    const meta = localStorage.getItem(CACHE_META_KEY);
    return meta ? JSON.parse(meta) : {};
  } catch (error) {
    console.error('Failed to get cache metadata:', error);
    return {};
  }
};

/**
 * Set cache timestamp for a specific key
 * @param {string} key - Cache key identifier
 */
export const setCacheTimestamp = key => {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    validateCacheKey(key);
    const meta = getCacheMeta();
    meta[key] = Date.now();
    localStorage.setItem(CACHE_META_KEY, JSON.stringify(meta));
  } catch (error) {
    console.error(`Failed to set cache timestamp for "${key}":`, error);
  }
};

/**
 * Check if cached data is still valid (within TTL)
 * @param {string} key - Cache key identifier
 * @param {number} ttl - Time to live in milliseconds
 * @returns {boolean} True if cache is valid
 */
export const isCacheValid = (key, ttl) => {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  try {
    validateCacheKey(key);
    if (typeof ttl !== 'number' || ttl <= 0) {
      console.error('TTL must be a positive number');
      return false;
    }

    const meta = getCacheMeta();
    const timestamp = meta[key];

    if (!timestamp) return false;

    const age = Date.now() - timestamp;
    return age < ttl;
  } catch (error) {
    console.error(`Failed to check cache validity for "${key}":`, error);
    return false;
  }
};

/**
 * Check if cached data is stale (beyond TTL but within stale threshold)
 * @param {string} key - Cache key identifier
 * @param {number} ttl - Time to live in milliseconds
 * @param {number} staleMultiplier - Multiplier for stale threshold (default 2x TTL)
 * @returns {boolean} True if cache is stale
 */
export const isCacheStale = (key, ttl, staleMultiplier = 2) => {
  if (!isLocalStorageAvailable()) {
    return true;
  }

  try {
    validateCacheKey(key);
    if (typeof ttl !== 'number' || ttl <= 0) {
      console.error('TTL must be a positive number');
      return true;
    }
    if (typeof staleMultiplier !== 'number' || staleMultiplier <= 0) {
      console.error('Stale multiplier must be a positive number');
      return true;
    }

    const meta = getCacheMeta();
    const timestamp = meta[key];

    if (!timestamp) return true;

    const age = Date.now() - timestamp;
    return age > ttl * staleMultiplier;
  } catch (error) {
    console.error(`Failed to check cache staleness for "${key}":`, error);
    return true;
  }
};

/**
 * Invalidate cache for a specific key
 * @param {string} key - Cache key identifier
 */
export const invalidateCache = key => {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    validateCacheKey(key);
    const meta = getCacheMeta();
    delete meta[key];
    localStorage.setItem(CACHE_META_KEY, JSON.stringify(meta));
  } catch (error) {
    console.error(`Failed to invalidate cache for "${key}":`, error);
  }
};

/**
 * Clear all cache metadata
 */
export const clearAllCache = () => {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    localStorage.removeItem(CACHE_META_KEY);
    localStorage.removeItem('cache_hits');
    localStorage.removeItem('cache_misses');
  } catch (error) {
    console.error('Failed to clear cache:', error);
  }
};

/**
 * Increment cache hit counter (for monitoring)
 */
export const incrementCacheHit = () => {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    const hits = parseInt(localStorage.getItem('cache_hits') || '0', 10);
    localStorage.setItem('cache_hits', (hits + 1).toString());
  } catch (error) {
    console.error('Failed to increment cache hit counter:', error);
  }
};

/**
 * Increment cache miss counter (for monitoring)
 */
export const incrementCacheMiss = () => {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    const misses = parseInt(localStorage.getItem('cache_misses') || '0', 10);
    localStorage.setItem('cache_misses', (misses + 1).toString());
  } catch (error) {
    console.error('Failed to increment cache miss counter:', error);
  }
};

/**
 * Get cache statistics
 * @returns {Object} Cache stats with hit rate
 */
export const getCacheStats = () => {
  if (!isLocalStorageAvailable()) {
    return {
      hits: 0,
      misses: 0,
      total: 0,
      hitRate: '0%',
      error: 'localStorage not available',
    };
  }

  try {
    const hits = parseInt(localStorage.getItem('cache_hits') || '0', 10);
    const misses = parseInt(localStorage.getItem('cache_misses') || '0', 10);
    const total = hits + misses;
    const hitRate = total > 0 ? ((hits / total) * 100).toFixed(2) : 0;

    return {
      hits,
      misses,
      total,
      hitRate: `${hitRate}%`,
    };
  } catch (error) {
    console.error('Failed to get cache stats:', error);
    return {
      hits: 0,
      misses: 0,
      total: 0,
      hitRate: '0%',
      error: error.message,
    };
  }
};

// Make stats available in development console
if (process.env.NODE_ENV === 'development') {
  window.getCacheStats = getCacheStats;
  window.clearAllCache = clearAllCache;
}
